import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// School-themed story icons with links - wszystkie wskazuja na ZYWE sciezki
const storyItems = [
    {
        id: 'search',
        icon: '🔍',
        label: 'Szukaj klasy',
        link: '/search',
        description: 'Znajdź swoją szkołę i klasę wśród 37 000+ placówek',
        color: 'from-blue-500 to-cyan-500'
    },
    {
        id: 'gallery',
        icon: '📸',
        label: 'Galeria',
        link: '/gallery',
        description: 'Przeglądaj zdjęcia klasowe i szkolne wspomnienia',
        color: 'from-pink-500 to-rose-500'
    },
    {
        id: 'groups',
        icon: '👥',
        label: 'Grupy',
        link: '/groups',
        description: 'Dołącz do grup absolwentów i dyskutuj',
        color: 'from-green-500 to-emerald-500'
    },
    {
        id: 'blog',
        icon: '📰',
        label: 'Blog',
        link: '/blog',
        description: 'Czytaj artykuly dla absolwentow - poradniki, wspomnienia, zjazdy',
        color: 'from-amber-500 to-yellow-500'
    },
    {
        id: 'zorki',
        icon: '⭐',
        label: 'Zorki',
        link: '/zorki',
        description: 'Zbieraj Zorki i kupuj wirtualne gadżety szkolne',
        color: 'from-purple-500 to-violet-500'
    },
    {
        id: 'referrals',
        icon: '🎁',
        label: 'Poleć',
        link: '/polecenia',
        description: 'Zaproś znajomych i zdobądź 5 Zorków za każdego!',
        color: 'from-orange-500 to-red-500'
    },
];

export const StoriesBar = () => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [selectedStory, setSelectedStory] = useState(null);
    const [schoolStats, setSchoolStats] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch school stats when user is logged in and has schools
    useEffect(() => {
        if (isAuthenticated && user?.schools?.length > 0) {
            fetchSchoolStats();
        }
    }, [isAuthenticated, user]);

    const fetchSchoolStats = async () => {
        try {
            const school = user?.schools?.[0];
            if (!school?.id) return;
            
            const response = await fetch(`${API_URL}/api/schools/${school.id}/stats`);
            if (response.ok) {
                const data = await response.json();
                setSchoolStats(data);
            }
        } catch (error) {
            console.error('Error fetching school stats:', error);
        }
    };

    const handleStoryClick = (story) => {
        // If requires auth and not logged in, show modal
        if ((story.id === 'referrals' || story.id === 'zorki') && !isAuthenticated) {
            setSelectedStory(story);
            return;
        }
        // Otherwise navigate directly
        navigate(story.link);
    };

    return (
        <>
            <div className="flex gap-3 overflow-x-auto pb-2 px-1 scrollbar-hide">
                {/* User's school quick access (if logged in and has school) */}
                {isAuthenticated && user?.schools?.length > 0 && (
                    <Link
                        to={`/szkola/${user.schools[0].slug || user.schools[0].id}`}
                        className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                    >
                        <div className="nk-story-circle">
                            <div className="nk-story-circle-inner">
                                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-2xl shadow-nk-sm group-hover:scale-105 transition-transform">
                                    🏫
                                </div>
                            </div>
                        </div>
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors max-w-16 truncate">
                            Moja szkoła
                        </span>
                        {schoolStats?.users_count > 0 && (
                            <span className="text-[10px] text-primary font-medium">
                                {schoolStats.users_count} osób
                            </span>
                        )}
                    </Link>
                )}

                {/* Main story items */}
                {storyItems.map((story, index) => (
                    <button
                        key={story.id}
                        onClick={() => handleStoryClick(story)}
                        className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                        style={{ animationDelay: `${index * 50}ms` }}
                    >
                        <div className="nk-story-circle">
                            <div className="nk-story-circle-inner">
                                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br ${story.color} flex items-center justify-center text-2xl shadow-nk-sm group-hover:scale-105 transition-transform`}>
                                    {story.icon}
                                </div>
                            </div>
                        </div>
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors max-w-16 truncate">
                            {story.label}
                        </span>
                    </button>
                ))}
                
                {/* Add story button - leads to adding school */}
                {!isAuthenticated ? (
                    <button 
                        onClick={() => setSelectedStory({ id: 'login', label: 'Zaloguj się' })}
                        className="flex flex-col items-center gap-1.5 flex-shrink-0 group opacity-70 hover:opacity-100 transition-opacity"
                    >
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center text-muted-foreground group-hover:border-primary group-hover:text-primary transition-colors">
                            <span className="text-2xl">+</span>
                        </div>
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                            Dołącz
                        </span>
                    </button>
                ) : !user?.schools?.length ? (
                    <Link
                        to="/search"
                        className="flex flex-col items-center gap-1.5 flex-shrink-0 group opacity-70 hover:opacity-100 transition-opacity"
                    >
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center text-muted-foreground group-hover:border-primary group-hover:text-primary transition-colors">
                            <span className="text-2xl">+</span>
                        </div>
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                            Dodaj szkołę
                        </span>
                    </Link>
                ) : null}
            </div>

            {/* Story Info Dialog */}
            <Dialog open={!!selectedStory} onOpenChange={() => setSelectedStory(null)}>
                <DialogContent className="nk-box p-0 max-w-sm overflow-hidden">
                    {selectedStory && (
                        <>
                            <DialogHeader className="nk-box-header">
                                <DialogTitle className="flex items-center gap-2 text-base">
                                    <span className="text-xl">{storyItems.find(s => s.id === selectedStory.id)?.icon || '📢'}</span>
                                    {selectedStory.label}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="p-4 text-center">
                                {selectedStory.id === 'login' ? (
                                    <>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Zaloguj się, aby uzyskać dostęp do wszystkich funkcji portalu!
                                        </p>
                                        <Button onClick={() => { setSelectedStory(null); navigate('/'); }} className="nk-button">
                                            Zaloguj się
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {selectedStory.description}
                                        </p>
                                        {!isAuthenticated && (
                                            <p className="text-xs text-amber-600 mb-4">
                                                Ta funkcja wymaga zalogowania
                                            </p>
                                        )}
                                        <Button 
                                            onClick={() => { setSelectedStory(null); navigate(selectedStory.link); }} 
                                            className="nk-button"
                                        >
                                            {isAuthenticated ? 'Przejdź' : 'Zaloguj i przejdź'}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
